import type { TranslateFn } from '../types'

const FALLBACK_PLACEHOLDER = /{{\s*(\w+)\s*}}/g

/**
 * Replaces `{{var}}` tokens in an English fallback string. Missing or null
 * values become an empty string so admin3 (no `t`) still shows readable copy.
 */
export const interpolateFallback = (
  template: string,
  values?: Record<string, unknown>,
): string => {
  if (!values) return template

  return template.replace(FALLBACK_PLACEHOLDER, (_match, key: string) => {
    const value = values[key]
    return value == null ? '' : String(value)
  })
}

export type Translator = (
  key: string,
  defaultValue: string,
  opts?: Record<string, unknown>,
) => string

/**
 * Returns a helper that resolves a translation key when a `t` function is
 * provided, and otherwise interpolates `{{var}}` tokens in the English default.
 *
 * This is intended for library components that accept an optional
 * `t?: TranslateFn` prop so the same component can render translated copy
 * in one host app and plain English defaults in another.
 *
 * @example
 *   const tr = createTranslator(props.t)
 *   tr('users.form.heading.editUser', 'Edit User')
 *   tr('users.form.userGroups.summary', '{{count}} groups selected', { count })
 */
export const createTranslator =
  (t?: TranslateFn): Translator =>
  (key, defaultValue, opts?) => {
    if (t) {
      const translated = t(key, { defaultValue, ...(opts ?? {}) })
      if (translated == null || translated === key) {
        return interpolateFallback(defaultValue, opts)
      }
      return translated
    }

    return interpolateFallback(defaultValue, opts)
  }

/**
 * Four-argument form of {@link createTranslator} used by feature libraries
 * that pass `t` into each call instead of closing over it.
 */
export const text = (
  t: TranslateFn | undefined,
  key: string,
  defaultValue: string,
  opts?: Record<string, unknown>,
): string => createTranslator(t)(key, defaultValue, opts)
